import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import { ORDERSTATUS } from 'utils/Constants';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';

import ProjectsPageHeader from 'components/shared/PMProjectsPageHeader';
import ProjectTable from 'components/shared/ProjectTable';

import 'pages/AdminClient/order.css';
import { consultantOrderListSelector } from 'store/Consultant/OrderList';
import { fetchConsultantAllOrders } from 'store/Consultant/OrderList/features';
import {
  CONSULTANT_FILTER_ROUTES,
  CONSULTANT_PROJECT_ROUTES,
  storeConsultantProjectDisplayId,
} from 'pages/consultant/projects/constants';
import ProjectSearchBar from 'components/shared/ProjectSearchBar';

const normalizeUrl = (url) => {
  if (!url) return '';
  return url
    .toString()
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '');
};

const getFilterFromSearch = (search) => {
  const filterParam = new URLSearchParams(search).get('filter');
  if (filterParam === ORDERSTATUS.ALL) return ORDERSTATUS.ALL;
  if (filterParam === ORDERSTATUS.NONATTRIBUTED) return ORDERSTATUS.NONATTRIBUTED;
  if (filterParam === ORDERSTATUS.INPROGRESS) return ORDERSTATUS.INPROGRESS;
  return ORDERSTATUS.INPROGRESS;
};

export default function ConsultantInProgressProjects() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();

  const {
    inProgressOrders = [],
    delayedOrders = [],
    validatedOrders = [],
    nonAttributedOrders = [],
    isLoading,
    isError,
  } = useSelector(consultantOrderListSelector);

  const [searchValue, setSearchValue] = useState('');
  const [activeFilter, setActiveFilter] = useState(() =>
    getFilterFromSearch(location.search)
  );
  const [selectedWebsiteUrl] = useWebsiteFilter();

  useEffect(() => {
    document.title = 'Typix - Mes projets';
  }, []);

  useEffect(() => {
    setActiveFilter(getFilterFromSearch(location.search));
  }, [location.search]);

  useEffect(() => {
    window.dispatchEvent(new Event('locationchange'));
    dispatch(fetchConsultantAllOrders());
  }, [dispatch]);

  const handleFilterChange = (status) => {
    if (status === ORDERSTATUS.DELAYED) {
      return history.push(CONSULTANT_PROJECT_ROUTES.DELAYED);
    }
    if (status === ORDERSTATUS.VALID) {
      return history.push(CONSULTANT_PROJECT_ROUTES.VALIDATED);
    }
    setActiveFilter(status);
    const search =
      status === ORDERSTATUS.INPROGRESS ? '' : `?filter=${status}`;
    history.replace(`${CONSULTANT_PROJECT_ROUTES.IN_PROGRESS}${search}`);
  };

  const getCurrentOrders = () => {
    switch (activeFilter) {
      case ORDERSTATUS.ALL:
        return [
          ...nonAttributedOrders,
          ...inProgressOrders,
          ...delayedOrders,
          ...validatedOrders,
        ];
      case ORDERSTATUS.NONATTRIBUTED:
        return nonAttributedOrders;
      case ORDERSTATUS.INPROGRESS:
        return inProgressOrders;
      default:
        return inProgressOrders;
    }
  };

  const filteredOrders = useMemo(() => {
    const orders = getCurrentOrders();
    const q = (searchValue || '').toString().trim().toLowerCase();

    const matchesSearch = (order) => {
      if (!q) return true;
      const haystack = [
        order?.title,
        order?.keyword,
        order?.url,
        order?.urlPage,
        order?.websiteUrl,
        order?.website?.urlSite,
      ]
        .filter(Boolean)
        .map((v) => v.toString().toLowerCase())
        .join(' ');
      return haystack.includes(q);
    };

    if (!selectedWebsiteUrl || !orders || orders.length === 0) {
      return (orders || []).filter(matchesSearch);
    }

    const normalizedSelectedUrl = normalizeUrl(selectedWebsiteUrl);
    return orders
      .filter((order) => {
        const orderWebsiteUrl = order.websiteUrl || order.website?.urlSite || '';
        return normalizeUrl(orderWebsiteUrl) === normalizedSelectedUrl;
      })
      .filter(matchesSearch);
  }, [
    selectedWebsiteUrl,
    activeFilter,
    inProgressOrders,
    delayedOrders,
    validatedOrders,
    nonAttributedOrders,
    searchValue,
  ]);

  const hasNoProjectsAtAll = useMemo(() => {
    const filterByWebsite = (orders) => {
      if (!Array.isArray(orders) || orders.length === 0) return [];
      if (!selectedWebsiteUrl) return orders;
      const normalizedSelectedUrl = normalizeUrl(selectedWebsiteUrl);
      return orders.filter((order) => {
        const orderWebsiteUrl = order.websiteUrl || order.website?.urlSite || '';
        return normalizeUrl(orderWebsiteUrl) === normalizedSelectedUrl;
      });
    };

    return (
      filterByWebsite(inProgressOrders).length === 0 &&
      filterByWebsite(delayedOrders).length === 0 &&
      filterByWebsite(validatedOrders).length === 0 &&
      filterByWebsite(nonAttributedOrders).length === 0
    );
  }, [
    selectedWebsiteUrl,
    inProgressOrders,
    delayedOrders,
    validatedOrders,
    nonAttributedOrders,
  ]);

  return (
    <div>
      <ProjectsPageHeader
        activeFilter={activeFilter}
        onFilterChange={handleFilterChange}
        filterRoutes={CONSULTANT_FILTER_ROUTES}
        hideNonAttributedFilter
        hideAddButton
      />

      <ProjectSearchBar
        searchValue={searchValue}
        setSearchValue={setSearchValue}
      />

      <ProjectTable
        orderStatus={activeFilter}
        orders={filteredOrders}
        isLoading={isLoading}
        isError={isError}
        hasNoProjectsAtAll={hasNoProjectsAtAll}
        fetchDisplayData={storeConsultantProjectDisplayId}
        onRowClickRedirectPath={CONSULTANT_PROJECT_ROUTES.DETAILS}
        hideTitle={true}
        hideSearch={true}
        disableWebsiteFilter={true}
        hidePaginationSelector={true}
        hideEmptyStateCreateProject
      />
    </div>
  );
}
